/**
 * Track history rendering for unit movement trails.
 * Supports both 2D (google.maps.Polyline) and 3D (Polyline3DElement) modes.
 * Uses per-unit affiliation colors for visual distinction.
 */

import type { NormalizedSaEvent } from '@vtak/shared';
import type { ViewMode, MapInstance } from './map-setup';
import { getAffiliationColor } from './marker-icons';

/** Maximum positions to keep per unit */
const MAX_TRACK_POINTS = 50;

/** Fallback track line color */
const DEFAULT_TRACK_COLOR = '#64b5f6';

/** Track line width for 2D dashed lines */
const TRACK_2D_SCALE = 2.5;

/** Track line width for 3D solid lines */
const TRACK_3D_WIDTH = 3;

/** Per-unit track color keyed by UID */
const trackColors = new Map<string, string>();

/** Position history keyed by UID, storing {lat, lng} arrays */
const positionHistory = new Map<string, google.maps.LatLngLiteral[]>();

/** Active 2D polylines keyed by UID */
const polylines2d = new Map<string, google.maps.Polyline>();

/** Active 3D polylines keyed by UID */
const polylines3d = new Map<string, google.maps.maps3d.Polyline3DElement>();

/** Active map reference for 3D cleanup */
let activeMap3d: google.maps.maps3d.Map3DElement | null = null;

/** Update track history with new events and re-render trails */
export function updateTrackHistory(
  map: MapInstance,
  mode: ViewMode,
  events: NormalizedSaEvent[],
): void {
  recordPositions(events);

  if (mode === '2d') {
    renderTracks2D(map as google.maps.Map);
  } else {
    activeMap3d = map as google.maps.maps3d.Map3DElement;
    renderTracks3D(map as google.maps.maps3d.Map3DElement);
  }
}

/** Clear all track history and rendered lines */
export function clearTrackHistory(mode: ViewMode): void {
  positionHistory.clear();
  trackColors.clear();
  clearRenderedTracks(mode);
}

/** Destroy all track resources */
export function destroyTracks(mode: ViewMode): void {
  clearTrackHistory(mode);
  activeMap3d = null;
}

/* ─── Internal: Position Recording ─── */

function recordPositions(events: NormalizedSaEvent[]): void {
  for (const evt of events) {
    const newPos: google.maps.LatLngLiteral = { lat: evt.lat, lng: evt.lon };
    let history = positionHistory.get(evt.uid);

    if (!history) {
      history = [];
      positionHistory.set(evt.uid, history);
      trackColors.set(evt.uid, getAffiliationColor(evt.type));
    }

    if (shouldAddPoint(history, newPos)) {
      history.push(newPos);
      trimHistory(history);
    }
  }
}

function shouldAddPoint(history: google.maps.LatLngLiteral[], newPos: google.maps.LatLngLiteral): boolean {
  const last = history[history.length - 1];
  if (!last) return true;
  return last.lat !== newPos.lat || last.lng !== newPos.lng;
}

function trimHistory(history: google.maps.LatLngLiteral[]): void {
  if (history.length > MAX_TRACK_POINTS) {
    history.shift();
  }
}

/* ─── Internal: 2D Rendering ─── */

function renderTracks2D(map: google.maps.Map): void {
  for (const [uid, history] of positionHistory) {
    if (history.length < 2) continue;

    const color = trackColors.get(uid) ?? DEFAULT_TRACK_COLOR;
    const existing = polylines2d.get(uid);

    if (existing) {
      existing.setPath(history);
      continue;
    }

    const polyline = new google.maps.Polyline({
      path: history,
      strokeColor: color,
      strokeOpacity: 0,
      icons: [buildDashedIcon(color)],
      map,
    });

    polylines2d.set(uid, polyline);
  }

  removeStalePolylines2D();
}

function buildDashedIcon(color: string): google.maps.IconSequence {
  return {
    icon: {
      path: 'M 0,-1 0,1',
      strokeOpacity: 0.6,
      scale: TRACK_2D_SCALE,
      strokeColor: color,
    },
    repeat: '8px',
  };
}

function removeStalePolylines2D(): void {
  for (const [uid, polyline] of polylines2d) {
    if (!positionHistory.has(uid)) {
      polyline.setMap(null);
      polylines2d.delete(uid);
    }
  }
}

/* ─── Internal: 3D Rendering ─── */

function renderTracks3D(map3d: google.maps.maps3d.Map3DElement): void {
  for (const [uid, history] of positionHistory) {
    if (history.length < 2) continue;

    const color = trackColors.get(uid) ?? DEFAULT_TRACK_COLOR;
    const coordinates = history.map((pos) => ({
      lat: pos.lat,
      lng: pos.lng,
      altitude: 0,
    }));

    const existing = polylines3d.get(uid);
    if (existing) {
      existing.coordinates = coordinates;
      continue;
    }

    const polyline = new google.maps.maps3d.Polyline3DElement({
      coordinates,
      altitudeMode: google.maps.maps3d.AltitudeMode.CLAMP_TO_GROUND,
      strokeColor: color,
      strokeWidth: TRACK_3D_WIDTH,
      drawsOccludedSegments: true,
    });

    polylines3d.set(uid, polyline);
    map3d.appendChild(polyline);
  }

  removeStalePolylines3D(map3d);
}

function removeStalePolylines3D(map3d: google.maps.maps3d.Map3DElement): void {
  for (const [uid, polyline] of polylines3d) {
    if (!positionHistory.has(uid)) {
      try {
        map3d.removeChild(polyline);
      } catch {
        /* polyline may already be removed */
      }
      polylines3d.delete(uid);
    }
  }
}

/* ─── Internal: Cleanup ─── */

function clearRenderedTracks(mode: ViewMode): void {
  if (mode === '2d') {
    for (const polyline of polylines2d.values()) {
      polyline.setMap(null);
    }
    polylines2d.clear();
  } else {
    for (const polyline of polylines3d.values()) {
      try {
        activeMap3d?.removeChild(polyline);
      } catch {
        /* already removed */
      }
    }
    polylines3d.clear();
  }
}
